import { Delete as DeleteIcon, LockOutlined as LockOutlineIcon } from "@mui/icons-material";
import { Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AsolviDatePicker } from "components/AsolviDatePicker";
import { AsolviTimePicker } from "components/AsolviTimePicker";
import { toDate } from "helpers";
import { JobVisitType } from "models/JobVisitType";
import { FC } from "react";
import { useIntl } from "react-intl";

const PREFIX = "TimeBlock";
const classes = {
  field: `${PREFIX}-field`,
};
const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.field}`]: {
    border: `1px solid ${theme.palette.error.main}`,
  },
}));

interface TimeBlockComponentProps {
  jobVisit: JobVisitType;
  id: number;
  setIndex: (index: number) => void;
  setOpenDeleteDialog: (open: boolean) => void;
  showLastOne?: boolean;
  timeBlockOnChange: (
    id: number,
    fieldname: "startDate" | "startTime" | "stopDate" | "stopTime",
    value: Date,
  ) => void;
  timesLabel: "workTimes" | "travelTimes";
  timesText: string;
  totalTime: string;
  disabled?: boolean;
  warning?: boolean;
}

const TimeBlockComponent: FC<TimeBlockComponentProps> = (props) => {
  const {
    jobVisit,
    id,
    setIndex,
    setOpenDeleteDialog,
    showLastOne,
    timeBlockOnChange,
    timesLabel,
    timesText,
    totalTime,
    disabled,
    warning
  } = props;
  const intl = useIntl();

  return (
    <StyledGrid container spacing={1}>
      <Grid item container direction="row" spacing={1} wrap="nowrap" justifyContent="space-between">
        <Grid item ml={1}>
          <Typography variant="body1">
            {timesText}: {totalTime}
          </Typography>
        </Grid>
        <Grid item mr={1}>
          {disabled ? (
            <LockOutlineIcon color={warning? "warning" : "primary"} />
          ) : (
            <DeleteIcon
              color="error"
              onClick={() => {
                setIndex(id);
                setOpenDeleteDialog(true);
              }}
              data-testid="TimeBlockComponent-DeleteButton"
            />
          )}
        </Grid>
      </Grid>
      <Grid item container direction="row" spacing={1} wrap="nowrap">
        <Grid item xs={6}>
          <AsolviDatePicker
            name={`${timesLabel}[${id}].startDate`}
            label={intl.formatMessage({ id: "times.startDate" })}
            required
            value={toDate(jobVisit[timesLabel][id].startDate)}
            onChange={(value: Date) => timeBlockOnChange?.(id, "startDate", value)}
            error={jobVisit.errors[`${timesLabel}[${id}].startDate`]}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6}>
          <AsolviTimePicker
            name={`${timesLabel}[${id}].startTime`}
            label={intl.formatMessage({ id: "times.startTime" })}
            required
            value={toDate(jobVisit[timesLabel][id].startTime)}
            onChange={(value: Date) => timeBlockOnChange?.(id, "startTime", value)}
            error={jobVisit.errors[`${timesLabel}[${id}].startTime`]}
            disabled={disabled}
          />
        </Grid>
      </Grid>
      {showLastOne && (
        <Grid item container direction="row" spacing={1} wrap="nowrap">
          <Grid item xs={6}>
            <AsolviDatePicker
              name={`${timesLabel}[${id}].stopDate`}
              label={intl.formatMessage({ id: "times.endDate" })}
              required
              value={toDate(jobVisit[timesLabel][id].stopDate)}
              onChange={(value: Date) => timeBlockOnChange?.(id, "stopDate", value)}
              error={jobVisit.errors[`${timesLabel}[${id}].stopDate`]}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={6}>
            <AsolviTimePicker
              name={`${timesLabel}[${id}].stopTime`}
              label={intl.formatMessage({ id: "times.endTime" })}
              required
              value={toDate(jobVisit[timesLabel][id].stopTime)}
              onChange={(value: Date) => timeBlockOnChange?.(id, "stopTime", value)}
              error={jobVisit.errors[`${timesLabel}[${id}].stopTime`]}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      )}
    </StyledGrid>
  );
};

export default TimeBlockComponent;
